import React, { useState, useEffect, useMemo, ReactNode, useRef, createContext, useContext } from 'react';
import { Outlet, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { isStudent, isTeacher, isSuperAdmin } from '../../utils/firebase';
import SmartNavbar from '../ui/SmartNavbar';
import NotificationDialog from '../ui/NotificationDialog';
import NoteDialog, { Note } from '../ui/NoteDialog';
import UserProfileMenuDialog from '../ui/UserProfileMenuDialog';
import { Notification } from '../../types/user';
import { subscribeToUserNotifications, markNotificationAsRead, markAllNotificationsAsRead, deleteNotification } from '../../utils/notifications';
import NoticeBanner from '../ui/NoticeBanner';
import Footer from '../ui/Footer';
import { AnnouncementItem, getActiveAnnouncements } from '../../utils/announcements';
import { canManageOwnContent } from '../../config/contentRoutes';
import { planMeets, requiredPlanForPath, resolveTeacherPlan } from '../../config/teacherPlans';
import TeacherPlanNotice from '../teacher/TeacherPlanNotice';
import { getTrainingHostByOwnerUid } from '../../utils/trainingHost';

// 모바일/데스크탑 레이아웃 스타일 상수
export const LAYOUT_STYLES = {
  mobile: {
    padding: {
      horizontal: '20px',
      vertical: '44px',
      top: '44px',
      bottom: '44px',
    },
    margin: {
      top: '60px', // 네비게이션 바 높이
    },
    maxWidth: '100%',
    gap: '10px',
  },
  desktop: {
    padding: {
      horizontal: '0px',
      vertical: '44px',
      top: '44px',
      bottom: '44px',
    },
    margin: {
      top: '60px',
      left: '24px', // 네비바 padding과 맞춤: 좌 24 + 본문 + 우 24 = 1440
    },
    maxWidth: '1392px', // 1440 - 24 - 24
    gap: '10px',
  },
  breakpoint: 768, // 모바일/데스크탑 구분 기준 (px)
} as const;

// LayoutContext 타입 정의
interface LayoutContextType {
  isMobile: boolean;
  windowSize: { width: number; height: number };
  scale: number;
  styles: typeof LAYOUT_STYLES;
}

// LayoutContext 생성
const LayoutContext = createContext<LayoutContextType | undefined>(undefined);

// LayoutContext를 사용하는 훅
export const useLayoutContext = () => {
  const context = useContext(LayoutContext);
  if (!context) {
    throw new Error('useLayoutContext must be used within Layout component');
  }
  return context;
};

// 화면 크기에 따른 스케일 계산 훅
export const useScale = () => {
  const [scale, setScale] = useState(1);
  const [windowSize, setWindowSize] = useState({ width: window.innerWidth, height: window.innerHeight });
  
  useEffect(() => {
    const calculateScale = () => {
      const windowWidth = window.innerWidth;
      const windowHeight = window.innerHeight;
      
      // 모바일(768px 이하)일 때는 스케일링을 적용하지 않음 (반응형 레이아웃 사용)
      if (windowWidth < 768) {
        setScale(1);
        setWindowSize({ width: windowWidth, height: windowHeight });
        return;
      }
      
      // 윈도우 크기 상태 업데이트
      setWindowSize({ width: windowWidth, height: windowHeight });
      
      // 피그마 디자인 크기 (1440×960)
      const designWidth = 1440;
      const designHeight = 960;
      
      // 가로/세로 비율 계산
      const scaleX = windowWidth / designWidth;   // 가로 기준 스케일
      const scaleY = windowHeight / designHeight; // 세로 기준 스케일
      
      // 가로가 작아질 때의 동작:
      // 1. 뷰포트 너비가 1440px 이상일 때: 스케일 1 유지, 여백만 점진적으로 줄어듦
      //    - 여백 = (windowWidth - 1440) / 2 (양쪽 균등)
      // 2. 뷰포트 너비가 1440px 미만일 때: 스케일을 점진적으로 적용
      //    - 스케일 = windowWidth / 1440px
      //    - 시각적 너비 = 1440px × scale = windowWidth
      //    - transformOrigin: 'center top'으로 가로 중앙 기준 스케일링
      //    - 최상위 컨테이너의 alignItems: 'center'로 중앙 정렬
      //    - 스케일로 인해 시각적 너비가 뷰포트 너비와 같아지므로 양쪽 여백이 균등하게 0으로 수렴
      let finalScale;
      
      if (windowWidth < designWidth && windowHeight < designHeight) {
        // 둘 다 작으면 더 제약적인 쪽을 선택 (화면에 완전히 들어가도록)
        finalScale = Math.min(scaleX, scaleY);
      } else if (windowWidth < designWidth) {
        // 가로만 작으면 가로 기준으로만 스케일
        // scaleX = windowWidth / 1440px이므로 점진적으로 스케일 적용
        // 시각적 너비 = 1440px × scaleX = windowWidth로 뷰포트에 정확히 맞음
        finalScale = scaleX;
      } else if (windowHeight < designHeight) {
        // 세로만 작으면 세로 기준으로만 스케일 (가로 여백 허용)
        finalScale = scaleY;
      } else {
        // 둘 다 크면 스케일을 1로 유지 (확대하지 않음)
        finalScale = Math.min(scaleX, scaleY);
      }
      
      // 스케일은 최대 1까지만 적용 (확대 방지)
      setScale(finalScale);
    };
    
    calculateScale();
    window.addEventListener('resize', calculateScale);
    
    return () => window.removeEventListener('resize', calculateScale);
  }, []);
  
  return { scale, windowSize };
};

interface LayoutProps {
  children?: ReactNode;
  showNavbar?: boolean;
  requireAuth?: boolean;
  navbarType?: 'teacher-class' | 'student-class' | 'subject' | 'notes' | 'notes-student' | 'notes-teacher' | 'class-materials-manage' | 'teacher-training-manage' | 'tools' | 'admin' | 'ib-grading' | 'minecraft-education' | 'public' | 'makecode-arcade' | 'info-chief';
}

const Layout: React.FC<LayoutProps> = ({ 
  children, 
  showNavbar = true,
  requireAuth = false,
  navbarType
}) => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { scale, windowSize } = useScale();
  const isMobile = windowSize.width < 768;
  const mainRef = useRef<HTMLElement | null>(null);
  
  // 상태 관리
  const [selectedToggle, setSelectedToggle] = useState(0); // 기본값: 학급운영
  const [selectedMenu, setSelectedMenu] = useState('dashboard');
  const [isNotificationOpen, setIsNotificationOpen] = useState(false);
  const [isNoteDialogOpen, setIsNoteDialogOpen] = useState(false);
  const [isUserProfileMenuOpen, setIsUserProfileMenuOpen] = useState(false);
  const [userMenuAnchor, setUserMenuAnchor] = useState<HTMLElement | null>(null);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [announcements, setAnnouncements] = useState<AnnouncementItem[]>([]);
  const [notes, setNotes] = useState<Note[]>([]);
  const [myTrainingSlug, setMyTrainingSlug] = useState<string | null>(null);
  const unreadNotificationCount = notifications.filter((notification) => !notification.isRead).length;

  // 네비게이션 타입 결정 (hooks는 항상 동일한 순서로 호출되어야 하므로 early return 전에 호출)
  const resolvedNavbarType = useMemo(() => {
    // navbarType이 명시적으로 제공된 경우 사용
    if (navbarType) {
      return navbarType;
    }

    // 현재 경로에 따라 자동 결정
    const path = location.pathname;
    if (path.startsWith('/makecodearcade')) {
      return 'makecode-arcade';
    }
    if (path.startsWith('/minecraft-education')) {
      return 'minecraft-education';
    }
    if (path.startsWith('/info-chief')) {
      return 'info-chief';
    }
    if (path.startsWith('/class-materials/manage')) {
      return 'class-materials-manage';
    }
    if (path.startsWith('/class-materials')) {
      return 'notes-student';
    }
    if (path.startsWith('/teacher-training/manage')) {
      return 'teacher-training-manage';
    }
    if (path.startsWith('/teacher-training')) {
      return 'notes-teacher';
    }
    
    // 로그인 페이지는 public navbar 사용
    if (path === '/login' || path.startsWith('/login')) {
      return 'public';
    }

    // 현재 경로와 사용자 권한에 따라 자동 결정
    if (selectedToggle === 1) {
      return 'subject'; // 교과운영
    } else {
      // 사용자 권한에 따라 다른 타입 반환
      if (currentUser && isStudent(currentUser)) {
        return 'student-class'; // 학생용 학급운영
      } else {
        return 'teacher-class'; // 교사용 학급운영
      }
    }
  }, [navbarType, location.pathname, selectedToggle, currentUser]);

  const usesFullMainContentWidth = useMemo(() => {
    if (isMobile || resolvedNavbarType === 'ib-grading') {
      return true;
    }
    const path = location.pathname;
    return /^\/teacher-training\/manage\/programs\/(new\/|[^/]+\/edit)/.test(path);
  }, [isMobile, resolvedNavbarType, location.pathname]);

  const isFullViewportKioskPage =
    location.pathname === '/class/teacher/SeatDisplay' ||
    location.pathname.startsWith('/class/kiosk/');

  // 로그인 페이지는 public navbar 사용
  const isPublicNavbar = resolvedNavbarType === 'public';
  const isArcadeDark = resolvedNavbarType === 'makecode-arcade';
  const arcadeDarkBackground = '#121218';

  // public(홈/로그인) 페이지에서 푸터 아래 여백 제거 효과를 적용하기 위한 useEffect 훅
  useEffect(() => {
    // 만약 현재 네비게이션 바 타입이 'public'(즉, 홈/로그인 등 퍼블릭 페이지)이면,
    if (isPublicNavbar) {
      // document.body에 'layout-public' 클래스를 추가해서
      // 전역적으로 min-height 등의 CSS 스타일을 해제하거나 수정할 수 있도록 한다.
      document.body.classList.add('layout-public');
      // cleanup 함수: 컴포넌트 언마운트 또는 isPublicNavbar가 false로 변경될 때 실행됨
      // body에서 'layout-public' 클래스를 제거하여 이전 상태로 되돌린다.
      return () => document.body.classList.remove('layout-public');
    }
    // isPublicNavbar가 false일 경우엔 아무 것도 하지 않는다.
    // (body에 class 추가/제거가 필요 없기 때문)
  }, [isPublicNavbar]); // isPublicNavbar 값이 변경될 때마다 이 effect가 실행된다.

  useEffect(() => {
    if (!currentUser?.uid) {
      setNotifications([]);
      return;
    }

    const unsubscribe = subscribeToUserNotifications(currentUser.uid, (fetchedNotifications) => {
      if (process.env.NODE_ENV === 'development') {
        // eslint-disable-next-line no-console
        console.debug('[Layout] fetchedNotifications:', fetchedNotifications);
      }
      setNotifications(fetchedNotifications);
    });

    return () => unsubscribe();
  }, [currentUser?.uid]);

  useEffect(() => {
    const fetchAnnouncements = async () => {
      const announcementList = await getActiveAnnouncements();
      setAnnouncements(announcementList);
    };

    fetchAnnouncements();
  }, [location.pathname]);

  useEffect(() => {
    if (!currentUser?.uid || !canManageOwnContent(currentUser.role)) {
      setMyTrainingSlug(null);
      return;
    }

    getTrainingHostByOwnerUid(currentUser.uid)
      .then((host) => setMyTrainingSlug(host?.publicSlug ?? null))
      .catch((err) => console.error(err));
  }, [currentUser?.uid, currentUser?.role]);

  const visibleAnnouncements = useMemo(() => {
    const path = location.pathname;
    const isHomePath = path === '/';
    const isStudentClassPath = path.startsWith('/class/student');
    const isTeacherClassPath = path.startsWith('/class/teacher');
    const userRole = currentUser?.role;

    return announcements.filter((announcement) => {
      const locationMatch =
        !announcement.displayLocation ||
        announcement.displayLocation === 'all' ||
        (announcement.displayLocation === 'home' && isHomePath) ||
        (announcement.displayLocation === 'class-student' && isStudentClassPath) ||
        (announcement.displayLocation === 'class-teacher' && isTeacherClassPath);

      if (!locationMatch) {
        return false;
      }

      if (announcement.targetAudience === 'all') {
        return true;
      }

      if (announcement.targetAudience === 'students') {
        return userRole === 'student';
      }

      if (announcement.targetAudience === 'teachers') {
        return userRole === 'teacher' || userRole === 'schoolAdmin' || userRole === 'superAdmin';
      }

      return true;
    });
  }, [announcements, currentUser?.role, location.pathname]);

  // 현재 경로에 따라 선택된 메뉴 상태 설정
  useEffect(() => {
    const path = location.pathname;
    
    // 수업 자료 허브
    if (path.startsWith('/class-materials')) {
      if (path.includes('/classroom')) {
        setSelectedMenu('classroom');
      } else if (path.includes('/manage')) {
        setSelectedMenu('my-manage');
      } else if (path.includes('/ai-robot-project') || path.includes('/Microbit')) {
        setSelectedMenu('ai-robot');
      } else {
        setSelectedMenu('materials');
      }
    }
    else if (path.startsWith('/class-materials/manage')) {
      setSelectedMenu('manage-home');
    }
    // 교사 연수 허브
    else if (path.startsWith('/teacher-training')) {
      if (path.startsWith('/teacher-training/manage')) {
        setSelectedMenu('my-manage');
      } else {
        const hubMatch = path.match(/^\/teacher-training\/h\/([^/]+)/);
        const isMyHub = hubMatch && myTrainingSlug && hubMatch[1] === myTrainingSlug;
        if (path === '/teacher-training/my' || isMyHub) {
          setSelectedMenu('lobby');
        } else {
          setSelectedMenu('training');
        }
      }
    }
    // 교과운영 경로인지 확인
    else if (path.startsWith('/subject/')) {
      setSelectedToggle(1); // 교과운영
      
      // 교과운영 메뉴 상태 설정
      if (path.includes('/settings')) {
        setSelectedMenu('settings');
      } else if (path.includes('/SubjectTeacherPage')) {
        setSelectedMenu('subject');
      } else if (path.includes('/ClassManagement')) {
        setSelectedMenu('subject');
      } else if (path.includes('/LessonManagement')) {
        setSelectedMenu('subject');
      } else if (path.includes('/AchievementManagement') || path.includes('/VoteManagement')) {
        setSelectedMenu('subject');
      } else {
        setSelectedMenu('dashboard');
      }
    }
    // IBGrading 경로인지 확인
    else if (path.startsWith('/ib-grading')) {
      if (path.includes('/subject-group-guide')) {
        setSelectedMenu('subject-group');
      } else if (path.includes('/final-grade-guide')) {
        setSelectedMenu('final-grade');
      } else {
        setSelectedMenu('guide');
      }
    }
    // MakeCode Arcade 경로인지 확인
    else if (path.startsWith('/makecodearcade')) {
      if (path === '/makecodearcade' || path.includes('/ArcadeMain')) {
        setSelectedMenu('home');
      } else if (path.includes('/chart')) {
        setSelectedMenu('chart');
      } else if (path.includes('/store')) {
        setSelectedMenu('store');
      } else if (path.includes('/sprite-gallery')) {
        setSelectedMenu('home');
      } else if (path.includes('/gallery')) {
        setSelectedMenu('gallery');
      } else if (path.includes('/explains')) {
        setSelectedMenu('explains');
      } else if (path.includes('/extensions')) {
        setSelectedMenu('extensions');
      } else if (path.includes('/register-guide')) {
        setSelectedMenu('register-guide');
      } else if (path.includes('/register/my-games')) {
        setSelectedMenu('my-games');
      } else if (path.includes('/register')) {
        setSelectedMenu('register-new');
      } else if (path.includes('/upload-code')) {
        setSelectedMenu('upload-code');
      } else if (path.includes('/class-management') || path.includes('/game-management') || path.includes('/student-management') || path.includes('/evaluation-code')) {
        setSelectedMenu('class-management');
      } else {
        setSelectedMenu('home');
      }
    }
    // Minecraft Education 경로인지 확인
    else if (path.startsWith('/minecraft-education')) {
      if (path.includes('/world-share')) {
        setSelectedMenu('world-share');
      } else if (path.includes('/mod-guide')) {
        setSelectedMenu('mod-guide');
      } else if (path.includes('/makecode')) {
        setSelectedMenu('makecode');
      } else {
        setSelectedMenu('home');
      }
    } else if (path.startsWith('/info-chief')) {
      setSelectedToggle(0);
      if (path.includes('/info-category')) {
        setSelectedMenu('info-category');
      } else if (path.includes('/privacy-category') || path.includes('/privacy-internal-management-plan')) {
        setSelectedMenu('privacy-category');
      } else if (path.includes('/device-materials')) {
        setSelectedMenu('info-chief-devices');
      } else if (path.includes('/url-access-list')) {
        setSelectedMenu('info-chief-url-access-list');
      } else if (path.includes('/related-sites')) {
        setSelectedMenu('info-chief-related-sites');
      } else {
        setSelectedMenu('info-chief-home');
      }
    } else {
      setSelectedToggle(0); // 학급운영

      if (path.startsWith('/class/student')) {
        if (path.includes('/MyHome')) {
          setSelectedMenu('my-home');
        } else if (path.includes('/StudentClassPoints') || path.includes('/point-approval')) {
          setSelectedMenu('points');
        } else if (path.includes('/ClassItemShop')) {
          setSelectedMenu('shop');
        } else if (path.includes('/ClassMyItems')) {
          setSelectedMenu('items');
        } else if (path.includes('/StudentSettings')) {
          setSelectedMenu('settings');
        } else if (path.includes('/MessageInbox')) {
          setSelectedMenu('messages');
        } else if (path.includes('/StudentAchievements')) {
          setSelectedMenu('achievements');
        } else if (
          path.includes('/Vote') ||
          path.includes('/RoleAuction') ||
          path.includes('/SeatReservation') ||
          path.includes('/VirtualPet') ||
          path.includes('/CharacterWardrobe') ||
          path.includes('/Manito') ||
          path.includes('/BirthdayRollingPaper')
        ) {
          setSelectedMenu('class-activities');
        } else {
          setSelectedMenu('dashboard');
        }
      } else if (path.includes('/ClassManagement') || path.includes('/SeatingManagement') || 
          path.includes('/ClassRoleManagement') || path.includes('/VoteManagement') || 
          path.includes('/AchievementManagement') || path.includes('/ManitoManagement') ||
          path.includes('/BirthdayManagement') || path.includes('/SeatManagement') ||
          path.includes('/AttendanceManagement') ||
          path.includes('/seating-management/')) {
        setSelectedMenu('management');
      } else if (path.includes('/ClassMaterials') || path.includes('/DailyMission') || 
                 path.includes('/RoleAuctionManagement')) {
        setSelectedMenu('operation');
      } else if (path.includes('/PointApprovalManagement') || path.includes('/point-approval') || path.includes('/ClassPointManagement')) {
        setSelectedMenu('points');
      } else if (path.includes('/StoreManagement') || path.includes('/ItemScanner') || path.includes('/ItemApproval')) {
        setSelectedMenu('shop');
      } else if (path.includes('/ScheduleManagement')) {
        setSelectedMenu('schedule');
      } else if (path.includes('/TeacherSetting')) {
        setSelectedMenu('settings');
      } else if (path.includes('/MessageInbox')) {
        setSelectedMenu('messages');
      } else {
        setSelectedMenu('dashboard');
      }
    }
  }, [location.pathname, myTrainingSlug]);

  // 페이지 전환 시 스크롤을 맨 위로 이동
  useEffect(() => {
    // 즉시 스크롤 초기화 함수
    const scrollToTop = () => {
      window.scrollTo(0, 0);
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
      if (mainRef.current) {
        mainRef.current.scrollTop = 0;
      }
    };
    
    // 즉시 실행
    scrollToTop();
    
    // requestAnimationFrame으로 다음 프레임에서도 실행 (렌더링 완료 후)
    const rafId1 = requestAnimationFrame(() => {
      scrollToTop();
      
      // 추가로 여러 시점에서 재시도 (비동기 렌더링 대응)
      const rafId2 = requestAnimationFrame(() => {
        scrollToTop();
        
        // 짧은 지연 후 재시도
        setTimeout(() => {
          scrollToTop();
        }, 0);
        
        // 더 긴 지연 후 재시도 (컴포넌트 마운트 완료 후)
        setTimeout(() => {
          scrollToTop();
        }, 10);
        
        // 최종 확인 (모든 렌더링 완료 후)
        setTimeout(() => {
          scrollToTop();
        }, 50);
      });
      
      return () => cancelAnimationFrame(rafId2);
    });
    
    return () => {
      cancelAnimationFrame(rafId1);
    };
  }, [location.pathname]);

  // LayoutContext 값 생성 (early return 전에 호출해야 함)
  const layoutContextValue: LayoutContextType = useMemo(() => ({
    isMobile,
    windowSize,
    scale,
    styles: LAYOUT_STYLES,
  }), [isMobile, windowSize, scale]);

  // 권한 체크
  if (requireAuth && (!currentUser || (!isStudent(currentUser) && !isTeacher(currentUser) && !isSuperAdmin(currentUser)))) {
    return <Navigate to="/" replace />;
  }

  // 토글 변경 처리 (학급운영 ↔ 교과운영 또는 수업 자료 ↔ 교사 연수)
  const handleToggleChange = (index: number) => {
    setSelectedToggle(index);
    
    // info-chief 타입인 경우
    if (navbarType === 'info-chief') {
      navigate('/info-chief');
      return;
    }
    
    // 기존 학급운영/교과운영 처리
    if (index === 0) {
      // 학급운영 모드로 전환
      if (currentUser && isStudent(currentUser)) {
        navigate('/class/student');
      } else {
        navigate('/class/teacher/ClassTeacherHome');
      }
    } else {
      // 교과운영 모드로 전환
      if (currentUser && isStudent(currentUser)) {
        navigate('/subject/student');
      } else {
        navigate('/subject/teacher/SubjectTeacherHome');
      }
    }
  };

  // 메뉴 클릭 처리
  const handleMenuClick = (menuId: string, path?: string) => {
    setSelectedMenu(menuId);
    
    // path가 제공된 경우 해당 경로로 이동
    if (path) {
      navigate(path);
      return;
    }
    
    // 수업 자료 메뉴 처리
    if (resolvedNavbarType === 'notes-student') {
      switch (menuId) {
        case 'materials':
          navigate('/class-materials/materials');
          break;
        case 'classroom':
          navigate('/class-materials/classroom');
          break;
        case 'ai-robot':
          navigate('/class-materials/ai-robot-project');
          break;
        case 'my-manage':
          navigate('/class-materials/manage');
          break;
        default:
          break;
      }
      return;
    }

    if (resolvedNavbarType === 'class-materials-manage') {
      switch (menuId) {
        case 'manage-home':
          navigate('/class-materials/manage');
          break;
        case 'public-view':
          navigate('/class-materials/materials');
          break;
        default:
          break;
      }
      return;
    }

    if (resolvedNavbarType === 'teacher-training-manage' || resolvedNavbarType === 'notes-teacher') {
      switch (menuId) {
        case 'training':
          navigate('/teacher-training');
          break;
        case 'lobby':
          navigate('/teacher-training/my');
          break;
        case 'my-manage':
          navigate('/teacher-training/manage');
          break;
        default:
          break;
      }
      return;
    }

    // IBGrading 메뉴 처리
    if (navbarType === 'ib-grading') {
      switch (menuId) {
        case 'guide':
          navigate('/ib-grading');
          break;
        case 'subject-group':
          navigate('/ib-grading/subject-group-guide');
          break;
        case 'final-grade':
          navigate('/ib-grading/final-grade-guide');
          break;
        case 'download':
          navigate('/ib-grading');
          // TODO: 다운로드 섹션으로 스크롤
          break;
        case 'contact':
          navigate('/ib-grading');
          // TODO: 문의 섹션으로 스크롤
          break;
        default:
          break;
      }
      return;
    }
    
    // MakeCode Arcade 메뉴 처리
    if (navbarType === 'makecode-arcade') {
      switch (menuId) {
        case 'home':
          navigate('/makecodearcade');
          break;
        case 'chart':
          navigate('/makecodearcade/chart');
          break;
        case 'store':
          navigate('/makecodearcade/store');
          break;
        case 'gallery':
          navigate('/makecodearcade/gallery');
          break;
        case 'explains':
          navigate('/makecodearcade/explains');
          break;
        case 'extensions':
          navigate('/makecodearcade/extensions');
          break;
        case 'register':
        case 'register-new':
          navigate('/makecodearcade/register');
          break;
        case 'my-games':
          navigate('/makecodearcade/register/my-games');
          break;
        case 'register-guide':
          navigate('/makecodearcade/register-guide');
          break;
        case 'game-management':
          navigate('/makecodearcade/game-management');
          break;
        case 'student-management':
          navigate('/makecodearcade/student-management');
          break;
        case 'evaluation-code':
          navigate('/makecodearcade/evaluation-code');
          break;
        case 'upload-code':
          navigate('/makecodearcade/upload-code');
          break;
        default:
          break;
      }
      return;
    }
    
    // Minecraft Education 메뉴 처리
    if (navbarType === 'minecraft-education') {
      switch (menuId) {
        case 'home':
          navigate('/minecraft-education');
          break;
        case 'world-share':
          navigate('/minecraft-education');
          // TODO: 월드 공유 섹션으로 스크롤
          break;
        case 'mod-guide':
          navigate('/minecraft-education');
          // TODO: 모드 안내 섹션으로 스크롤
          break;
        case 'makecode':
          navigate('/minecraft-education');
          // TODO: Makecode 섹션으로 스크롤
          break;
        default:
          break;
      }
      return;
    }
    
    // info-chief 메뉴 처리
    if (navbarType === 'info-chief') {
      switch (menuId) {
        case 'info-chief-home':
          navigate('/info-chief');
          break;
        case 'info-category':
          navigate('/info-chief/info-category');
          break;
        case 'privacy-category':
          navigate('/info-chief/privacy-category');
          break;
        case 'info-chief-devices':
          navigate('/info-chief/device-materials');
          break;
        case 'info-chief-url-access-list':
          navigate('/info-chief/url-access-list');
          break;
        case 'info-chief-related-sites':
          navigate('/info-chief/related-sites');
          break;
        default:
          navigate('/info-chief');
          break;
      }
      return;
    }
    
    // 메뉴별 페이지 이동 (기본 경로)
    if (selectedToggle === 1) {
      // 교과운영 모드
      switch (menuId) {
        case 'dashboard':
          navigate('/subject/teacher/SubjectTeacherHome');
          break;
        case 'subjects':
          navigate('/subject/teacher/SubjectTeacherPage');
          break;
        case 'lessons':
          navigate('/subject/teacher/SubjectClassManagement');
          break;
        default:
          break;
      }
    } else {
      // 학급운영 모드
      if (currentUser && isStudent(currentUser)) {
        // 학생용 메뉴 처리
        switch (menuId) {
          case 'dashboard':
            navigate('/class/student');
            break;
          case 'points':
            navigate('/class/student/StudentClassPoints');
            break;
          case 'achievements':
            navigate('/class/student/StudentAchievements');
            break;
          case 'shop':
            navigate('/class/student/ClassItemShop');
            break;
          case 'items':
            navigate('/class/student/ClassMyItems');
            break;
          case 'stamps':
            navigate('/class/student/StudentStamp');
            break;
          case 'activities':
            navigate('/class/student/Vote');
            break;
          case 'settings':
            navigate('/class/student/StudentSettings');
            break;
          default:
            break;
        }
      } else {
        // 교사용 메뉴 처리
        switch (menuId) {
          case 'dashboard':
            navigate('/class/teacher/ClassTeacherHome');
            break;
          case 'management':
            navigate('/class/teacher/ClassManagement');
            break;
          case 'operation':
            navigate('/class/teacher/operation');
            break;
          case 'points':
            navigate('/class/teacher/PointApprovalManagement');
            break;
          case 'shop':
            navigate('/class/teacher/StoreManagement');
            break;
          case 'schedule':
            navigate('/class/teacher/ScheduleManagement');
            break;
          case 'settings':
            navigate('/class/teacher/TeacherSetting');
            break;
          default:
            break;
        }
      }
    }
  };

  // 채팅 클릭 처리
  const handleChatClick = () => {
    setIsNoteDialogOpen(true);
  };

  // 알림 클릭 처리
  const handleNotificationClick = () => {
    setIsNotificationOpen(true);
  };

  // 사용자 프로필 클릭 처리
  const handleUserClick = (event?: React.MouseEvent<HTMLElement>) => {
    if (event?.currentTarget) {
      setUserMenuAnchor(event.currentTarget);
    }
    setIsUserProfileMenuOpen(true);
  };

  // 로고 클릭 처리 (홈으로 이동)
  const handleLogoClick = () => {
    navigate('/');
  };

  // 가로가 1440보다 작으면 왼쪽 정렬, 그렇지 않으면 중앙 정렬
  const shouldCenterAlign = windowSize.width >= 1440;
  
  // 네비게이션 바 높이 (60px)
  const navbarHeight = 60;
  
  // 스케일이 적용될 때 컨테이너의 실제 높이 계산
  // 네비게이션 바가 스케일된 컨테이너 안에 있으므로, 전체 높이를 뷰포트에 맞춰야 함
  // 스케일 적용 후 전체 높이 = windowSize.height
  // 전체 높이 = 네비게이션 바 높이(스케일 적용) + 컨텐츠 높이(스케일 적용)
  // 전체 높이 = (네비게이션 바 실제 높이 + 컨텐츠 실제 높이) × scale
  // 따라서: 컨텐츠 실제 높이 = (windowSize.height / scale) - 네비게이션 바 실제 높이
  // 전체 컨테이너 실제 높이 = windowSize.height / scale
  const scaledContainerHeight = scale < 1 && windowSize.height < 960
    ? windowSize.height / scale
    : undefined;
  
  // 컨테이너의 실제 너비 계산
  // 양쪽 여백이 균등하게 줄어들도록 하기 위해:
  // 1. 뷰포트 너비가 1440px 이상일 때: 컨테이너 너비 1440px, 스케일 1
  //    - 여백 = (windowWidth - 1440) / 2 (양쪽 균등하게 줄어듦)
  // 2. 뷰포트 너비가 1440px 미만일 때: 컨테이너 너비는 1440px 유지, 스케일만 적용
  //    - transform: scale()은 레이아웃 공간에 영향을 주지 않으므로 레이아웃 공간은 여전히 1440px
  //    - 하지만 transformOrigin: 'center top'으로 가로 중앙 기준 스케일링하므로
  //    - 시각적 너비 = 1440px × scale = windowWidth (양쪽 여백이 균등하게 0으로 수렴)
  //    - 최상위 컨테이너의 alignItems: 'center'로 중앙 정렬
  //    - 레이아웃 공간이 1440px이므로 중앙 정렬 시 여백 = (windowWidth - 1440) / 2 (음수)
  //    - 하지만 overflow: hidden과 transform: scale()로 인해 시각적으로는 양쪽 여백이 균등하게 0
  // 
  // 문제: 레이아웃 공간이 1440px이므로 뷰포트가 작을 때 잘릴 수 있음
  // 해결: 스케일 적용 시 컨테이너 너비를 동적으로 조정하여 레이아웃 공간도 줄임
  // 하지만 여백이 균등하게 줄어들도록 하기 위해서는:
  // - containerWidth를 조정하면 레이아웃 공간이 줄어들어 중앙 정렬 시 여백이 달라짐
  // - 실제로는 containerWidth = 1440px로 유지하고, 스케일만 적용하는 것이 더 자연스러움
  // - 하지만 레이아웃 공간이 1440px이므로 뷰포트가 작을 때 잘릴 수 있음
  // 
  // 최종 해결책: 컨테이너 너비는 항상 1440px로 유지
  // - transformOrigin: 'center top'으로 가로 중앙 기준 스케일링
  // - 최상위 컨테이너의 alignItems: 'center'로 중앙 정렬
  // - 스케일로 인해 시각적 너비가 뷰포트 너비와 같아지므로 양쪽 여백이 균등하게 0으로 수렴
  // - overflow: hidden으로 인해 잘린 부분은 보이지 않음
  const containerWidth = 1440; // 항상 1440px로 고정 (여백이 먼저 줄어들고, 그 다음 스케일 적용)
  const requiredPlan = requiredPlanForPath(location.pathname);
  const planBlocked = Boolean(
    currentUser &&
    requiredPlan &&
    !planMeets(resolveTeacherPlan(currentUser.plan, currentUser.role), requiredPlan)
  );

  // 이 부분부터 UI 렌더링 파트가 맞는지 확인하는 주석 같은데, 네 맞아요. 여기서부터 실제 리액트 반환(return)에 들어가는 JSX가 시작됩니다.
  return (
    <LayoutContext.Provider value={layoutContextValue}>
      <div
        data-layout-root
        className={isPublicNavbar ? 'layout-public' : undefined}
        style={{
        width: '100%',
        height: 'auto',
        minHeight: undefined,
        position: 'relative',
        background: isArcadeDark ? arcadeDarkBackground : (showNavbar ? (isPublicNavbar ? 'white' : 'var(--Color-background-main, #F4F6FA)') : 'white'),
        // public 랜딩(Home) 같은 경우는 전체 페이지 스크롤 허용 (모바일 포함)
        // 모바일일 때는 public navbar인 경우에만 스크롤 허용
        // 데스크톱에서도 class/teacher, class/student 페이지는 스크롤 방지
        overflow: 'auto',
        overflowY: 'auto',
        overflowX: 'auto',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center', // 항상 중앙 정렬하여 양쪽 여백이 균등하게 줄어들도록
        justifyContent: 'flex-start' // 상단부터 시작
        // 스케일이 적용될 때도 양쪽 여백이 균등하게 줄어들도록 보장
        // transform: scale()은 레이아웃 공간에 영향을 주지 않으므로,
        // 컨테이너의 실제 너비(1440px)를 기준으로 중앙 정렬
        // transformOrigin: 'center top'으로 가로 중앙 기준 스케일링하므로
        // 시각적으로는 양쪽 여백이 균등하게 0으로 수렴
      }}>
      {/* SmartNavbar - IBGrading의 경우 fixed로 외부에 배치 */}
      {showNavbar && resolvedNavbarType === 'ib-grading' && (
        <SmartNavbar 
          type={resolvedNavbarType}
          selectedToggleIndex={selectedToggle}
          selectedMenuId={selectedMenu}
          onToggleChange={handleToggleChange}
          onMenuClick={handleMenuClick}
          onChatClick={handleChatClick}
          onNotificationClick={handleNotificationClick}
          onUserClick={handleUserClick}
          onLogoClick={handleLogoClick}
          unreadNotificationCount={unreadNotificationCount}
          currentUser={currentUser}
        />
      )}
      
      {/* 전체 페이지 컨테이너 - 스케일 없음(브라우저 확대/축소 정상 동작) */}
      <div
        data-layout-viewport
        style={{
        width: isMobile ? '100%' : '100%',
        maxWidth: isMobile ? '100%' : `${containerWidth}px`, // 데스크톱: 최대 1440px, 작은 화면에서는 100%로 유동
        minHeight: 'auto',
        height: 'auto',
        background: isArcadeDark ? arcadeDarkBackground : (showNavbar ? (isPublicNavbar ? 'white' : 'var(--Color-background-main, #F4F6FA)') : 'white'),
        overflow: 'auto',
        overflowX: 'auto',
        overflowY: 'auto',
        margin: '0',
        marginTop: showNavbar && resolvedNavbarType !== 'ib-grading' ? '0' : 'auto',
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        flexShrink: 0
      }}>
        {/* SmartNavbar - 조건부 렌더링 (IBGrading이 아닌 경우) */}
        {showNavbar && resolvedNavbarType !== 'ib-grading' && (
          <SmartNavbar 
            type={resolvedNavbarType}
            selectedToggleIndex={selectedToggle}
            selectedMenuId={selectedMenu}
            onToggleChange={handleToggleChange}
            onMenuClick={handleMenuClick}
            onChatClick={handleChatClick}
            onNotificationClick={handleNotificationClick}
            onUserClick={handleUserClick}
            onLogoClick={handleLogoClick}
            unreadNotificationCount={unreadNotificationCount}
            currentUser={currentUser}
          />
        )}
        
        {/* 메인 컨텐츠 영역 */}
        {showNavbar ? (
          resolvedNavbarType === 'public' ? (
            <div
              style={{
                width: '100%',
                padding: 0,
                boxSizing: 'border-box',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'flex-start',
                alignItems: 'stretch'
              }}
            >
              <NoticeBanner announcements={visibleAnnouncements} />
              <div>{children || <Outlet />}</div>
            </div>
          ) : (
          <>
          <main 
            ref={mainRef}
            style={{
            // 너비: 모바일/데스크탑 통합 관리
            width: resolvedNavbarType === 'ib-grading' 
              ? '100%' 
              : (isMobile ? LAYOUT_STYLES.mobile.maxWidth : LAYOUT_STYLES.desktop.maxWidth),
            // 높이: 모바일/데스크탑 통합 관리
            minHeight: isMobile 
              ? undefined // 모바일: height로 처리
              : 0, // 데스크탑: flex로 조정
            height: 'auto',
            maxHeight: undefined,
            // 패딩: 모바일/데스크탑 통합 관리
            padding: resolvedNavbarType === 'ib-grading' 
              ? '60px 0 0 0' 
              : (isMobile 
                  ? `${LAYOUT_STYLES.mobile.padding.top} ${LAYOUT_STYLES.mobile.padding.horizontal} ${LAYOUT_STYLES.mobile.padding.bottom} ${LAYOUT_STYLES.mobile.padding.horizontal}`
                  : `${LAYOUT_STYLES.desktop.padding.top} ${LAYOUT_STYLES.desktop.padding.horizontal} ${LAYOUT_STYLES.desktop.padding.bottom} ${LAYOUT_STYLES.desktop.padding.horizontal}`),
            // 마진: 모바일/데스크탑 통합 관리
            margin: resolvedNavbarType === 'ib-grading' 
              ? '0' 
              : (isMobile 
                  ? `${LAYOUT_STYLES.mobile.margin.top} 0 0 0`
                  : `${LAYOUT_STYLES.desktop.margin.top} auto 0 ${LAYOUT_STYLES.desktop.margin.left}`),
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'flex-start',
            alignItems: 'flex-start',
            // gap: 모바일/데스크탑 통합 관리
            gap: isMobile ? LAYOUT_STYLES.mobile.gap : LAYOUT_STYLES.desktop.gap,
            overflow: 'auto',
            overflowY: 'auto',
            overflowX: 'auto',
            flex: '1 1 auto',
            boxSizing: 'border-box' // 패딩 포함한 높이 계산
          }}>
            {/* 데스크톱: 1280px 가운데 정렬(높이 제한 없음), 모바일/ib-grading/연수 에디터: 100% */}
            <div
              style={{
                width: usesFullMainContentWidth ? '100%' : 1280,
                marginLeft: usesFullMainContentWidth ? undefined : 'auto',
                marginRight: usesFullMainContentWidth ? undefined : 'auto',
                flex: 1,
                minHeight: 0,
                display: 'flex',
                flexDirection: 'column',
                boxSizing: 'border-box',
                color: isArcadeDark ? '#F4F4F8' : undefined,
              }}
            >
              <NoticeBanner announcements={visibleAnnouncements} />
              {planBlocked ? <TeacherPlanNotice /> : (children || <Outlet />)}
            </div>
          </main>
          {resolvedNavbarType === 'makecode-arcade' && <Footer />}
          </>
          )
        ) : isFullViewportKioskPage ? (
          <div
            style={{
              width: '100%',
              height: '100%',
              overflow: 'hidden'
            }}
          >
            {children || <Outlet />}
          </div>
        ) : (
          // navbar 없을 때는 children만 렌더링 (스크롤 가능)
          <div style={{
            width: '100%',
            height: '100%',
            overflowY: 'auto',
            overflowX: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center'
          }}>
            <div style={{ flex: 1, width: '100%' }}>{children || <Outlet />}</div>
          </div>
        )}
      </div>
      
      {/* 알림 다이얼로그 */}
      {showNavbar && navbarType !== 'public' && (
        <NotificationDialog
          isOpen={isNotificationOpen}
          onClose={() => setIsNotificationOpen(false)}
          scale={scale}
          notifications={notifications}
          onMarkAsRead={async (notificationId) => {
            if (!currentUser?.uid) return;
            try {
              await markNotificationAsRead(currentUser.uid, notificationId);
            } catch (error) {
              console.error('글로벌 알림 읽음 처리 중 오류:', error);
            }
          }}
          onMarkAllAsRead={async () => {
            if (!currentUser?.uid) return;
            try {
              await markAllNotificationsAsRead(currentUser.uid);
            } catch (error) {
              console.error('글로벌 알림 전체 읽음 처리 중 오류:', error);
            }
          }}
          onDeleteNotification={async (notificationId) => {
            if (!currentUser?.uid) return;
            try {
              await deleteNotification(currentUser.uid, notificationId);
            } catch (error) {
              console.error('글로벌 알림 삭제 중 오류:', error);
            }
          }}
          onViewAll={() => {
            setIsNotificationOpen(false);
            navigate(currentUser && isStudent(currentUser)
              ? '/class/student/MessageInbox'
              : '/class/teacher/MessageInbox');
          }}
        />
      )}

      {/* 쪽지 다이얼로그 */}
      {showNavbar && navbarType !== 'public' && (
        <NoteDialog
          isOpen={isNoteDialogOpen}
          onClose={() => setIsNoteDialogOpen(false)}
          notes={notes}
          onMarkAsRead={async (noteId) => {
            // TODO: 쪽지 읽음 처리 로직 구현
            console.log('쪽지 읽음 처리:', noteId);
            setNotes(prev =>
              prev.map(note => note.id === noteId ? { ...note, isRead: true } : note)
            );
          }}
          onMarkAllAsRead={async () => {
            // TODO: 모든 쪽지 읽음 처리 로직 구현
            console.log('모든 쪽지 읽음 처리');
            setNotes(prev =>
              prev.map(note => ({ ...note, isRead: true }))
            );
          }}
          onNoteClick={(noteId) => {
            // TODO: 쪽지 클릭 처리 로직 구현 (쪽지 상세 페이지로 이동 등)
            console.log('쪽지 클릭:', noteId);
          }}
        />
      )}

      {/* 사용자 프로필 메뉴 다이얼로그 */}
      {/* resolvedNavbarType: 로그인 등에서 navbarType 미지정 시에도 public 여부 일치 */}
      {showNavbar && resolvedNavbarType !== 'public' && (
        <UserProfileMenuDialog
          isOpen={isUserProfileMenuOpen}
          onClose={() => {
            setIsUserProfileMenuOpen(false);
            setUserMenuAnchor(null);
          }}
          anchorElement={userMenuAnchor}
        />
      )}
      </div>
    </LayoutContext.Provider>
  );
};

export default Layout;
